import { z } from 'zod';
import { base64UrlToBytes, bytesToBase64Url } from '../encoding';
import { withinOptionalSearchDeadline } from './search-deadline';

export const MARKETPLACE_DB_CLAIM_MAX_TTL_SECONDS = 60;
export const MARKETPLACE_DB_CLAIM_DEFAULT_TTL_SECONDS = 30;
export const MARKETPLACE_DB_CLAIM_SECRET_MIN_BYTES = 32;
export const MARKETPLACE_DB_CLAIM_MAX_PAYLOAD_BYTES = 96 * 1024;
export const MARKETPLACE_DB_CLAIM_MAX_TOKEN_LENGTH = 131_200;

export const MarketplaceDbClaimTokenSchema = z.string()
  .min(80)
  .max(MARKETPLACE_DB_CLAIM_MAX_TOKEN_LENGTH)
  .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/);

export const MarketplaceDbClaimAudienceSchema = z.enum([
  'vhire_marketplace_authority_rw',
  'vhire_marketplace_search_ro',
]);

export const MarketplaceDbClaimPayloadSchema = z.object({
  v: z.literal(1),
  aud: MarketplaceDbClaimAudienceSchema,
  op: z.string().regex(/^marketplace\.[a-z0-9_.-]{1,80}$/),
  tenantId: z.string().uuid(),
  actorId: z.string().trim().min(1).max(128),
  requestId: z.string().uuid(),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
  nonce: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
  resource: z.record(z.unknown()),
}).strict().superRefine((payload, ctx) => {
  if (payload.exp <= payload.iat) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['exp'],
      message: 'Database claim must expire after it is issued',
    });
  }
  if (payload.exp - payload.iat > MARKETPLACE_DB_CLAIM_MAX_TTL_SECONDS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['exp'],
      message: 'Database claim lifetime exceeds 60 seconds',
    });
  }
});

export type MarketplaceDbClaimAudience = z.infer<typeof MarketplaceDbClaimAudienceSchema>;
export type MarketplaceDbClaimPayload = z.infer<typeof MarketplaceDbClaimPayloadSchema>;
export type MarketplaceDbClaimToken = z.infer<typeof MarketplaceDbClaimTokenSchema>;

function assertClaimSecret(secret: unknown): string {
  if (
    typeof secret !== 'string'
    || new TextEncoder().encode(secret).byteLength < MARKETPLACE_DB_CLAIM_SECRET_MIN_BYTES
  ) {
    throw new Error('Marketplace database claim signer is unavailable');
  }
  return secret;
}

function serializePayload(payload: MarketplaceDbClaimPayload): Uint8Array<ArrayBuffer> {
  const encodedSource = new TextEncoder().encode(JSON.stringify(payload));
  // Web Crypto's BufferSource excludes SharedArrayBuffer in DOM typings. Copy
  // into an owned ArrayBuffer-backed view so browser and Worker builds agree.
  const encoded = new Uint8Array(encodedSource.byteLength);
  encoded.set(encodedSource);
  if (encoded.byteLength > MARKETPLACE_DB_CLAIM_MAX_PAYLOAD_BYTES) {
    throw new Error('Marketplace database claim payload is too large');
  }
  return encoded;
}

/**
 * Mint a short-lived database capability. The exact payload bytes are signed;
 * PostgreSQL verifies those bytes before parsing or trusting tenant/resource
 * fields. Only the gateway receives the matching signing secrets. Candidate
 * and Search workers merely pass the opaque token to owner-owned definer APIs.
 */
export async function mintMarketplaceDbClaim(input: {
  secret: string;
  audience: MarketplaceDbClaimAudience;
  operation: string;
  tenantId: string;
  actorId: string;
  resource: Record<string, unknown>;
  nowMs?: number;
  ttlSeconds?: number;
  requestId?: string;
  nonce?: string;
  /** Read execution budget only; never included in the signed capability. */
  deadlineAt?: number;
}): Promise<string> {
  const nowSeconds = Math.floor((input.nowMs ?? Date.now()) / 1_000);
  const ttlSeconds = input.ttlSeconds ?? MARKETPLACE_DB_CLAIM_DEFAULT_TTL_SECONDS;
  if (!Number.isInteger(ttlSeconds) || ttlSeconds < 1 || ttlSeconds > MARKETPLACE_DB_CLAIM_MAX_TTL_SECONDS) {
    throw new Error('Marketplace database claim lifetime is invalid');
  }
  const payload = MarketplaceDbClaimPayloadSchema.parse({
    v: 1,
    aud: input.audience,
    op: input.operation,
    tenantId: input.tenantId,
    actorId: input.actorId,
    requestId: input.requestId ?? crypto.randomUUID(),
    iat: nowSeconds,
    exp: nowSeconds + ttlSeconds,
    nonce: input.nonce ?? crypto.randomUUID(),
    resource: input.resource,
  });
  const payloadBytes = serializePayload(payload);
  const key = await withinOptionalSearchDeadline(input.deadlineAt, 'marketplace-claim-key', () => crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(assertClaimSecret(input.secret)),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  ));
  const signature = new Uint8Array(await withinOptionalSearchDeadline(input.deadlineAt, 'marketplace-claim-sign',
    () => crypto.subtle.sign('HMAC', key, payloadBytes)));
  return `${bytesToBase64Url(payloadBytes)}.${bytesToBase64Url(signature)}`;
}

/** Test/diagnostic parser only. It deliberately does not authenticate. */
export function decodeUnverifiedMarketplaceDbClaim(token: string): MarketplaceDbClaimPayload {
  try {
    MarketplaceDbClaimTokenSchema.parse(token);
    const [payload] = token.split('.');
    const bytes = base64UrlToBytes(payload!);
    if (bytes.byteLength > MARKETPLACE_DB_CLAIM_MAX_PAYLOAD_BYTES) {
      throw new Error('Marketplace database claim payload is too large');
    }
    return MarketplaceDbClaimPayloadSchema.parse(
      JSON.parse(new TextDecoder().decode(bytes)),
    );
  } catch {
    throw new Error('Marketplace database claim is malformed');
  }
}
